/** Touch-input detection for Fabric pointer events.
 *
 *  Fabric v6 delivers the underlying DOM event (`opt.e`) as EITHER a
 *  `PointerEvent` — which carries `pointerType` ('mouse' | 'pen' |
 *  'touch') — OR a raw `TouchEvent`, which has no `pointerType` at all.
 *  Which one you get depends on the browser: many mobile browsers fire
 *  TouchEvents here (the same reason `clientPointFromEvent` already has a
 *  `'touches' in e` branch).
 *
 *  Gating touch behavior on `pointerType === 'touch'` alone therefore
 *  silently misses the TouchEvent case, which broke every touch-only
 *  affordance in the editor: tap-to-place tools (text / shape / fill /
 *  polygon / wand) committed on press instead of deferring to release
 *  (so a two-finger pan dropped a stray object on every touch), and the
 *  touch double-tap handlers (text word/select-all, pen-path finish,
 *  polygon close) never armed.
 *
 *  `isTouchEvent` detects BOTH shapes. Mouse and pen are correctly
 *  excluded: a mouse PointerEvent reports `pointerType === 'mouse'`, a
 *  stylus reports `'pen'`, and neither is a TouchEvent — so desktop and
 *  stylus behavior is unchanged. */
export function isTouchEvent(e: Event | undefined | null): boolean {
  if (!e) return false;
  // PointerEvent (and only PointerEvent) exposes `pointerType`. Trust it
  // when present so touch/mouse/pen are distinguished precisely.
  if ('pointerType' in e) return (e as PointerEvent).pointerType === 'touch';
  // No `pointerType` → a TouchEvent (touchstart/move has `touches`,
  // touchend has `changedTouches`) or a legacy MouseEvent (neither).
  return 'touches' in e || 'changedTouches' in e;
}
